import T from "@marginly/ui/components/typography";
import Image from "next/image";
import NextLink from "next/link";
import React from "react";

import { getTokenSymbol } from "@/app/util";
import { PoolProgress } from "@/components/common/pool-progress";
import { getTokenLargeImagePath } from "@/components/common/token";
import { useWeb3Context } from "@/contexts/web3";
import { usePoolCoeffs, usePoolData } from "@/hooks/position";
import { useApply } from "@/hooks/util";
import { Bonus } from "@/types/common";
import { daysDisplay } from "@/util/days-display";
import { daysTillMaturity } from "@/util/get-day-difference";

import { formatPercent } from "../../../util/formatters";
import { badgeByBonus } from "../../common/bonus/badge-by-bonus";
import { getCardInfo } from "./get-card-info";
import * as S from "./styled";

interface Props {
  poolAddress: `0x${string}`;
  isBaseToken: boolean;
  apr: number;
  chain: string;
  bonus?: Bonus[];
  totalCollateral: number;
  totalDebt: number;
}

export function Card({
  poolAddress,
  isBaseToken,
  apr,
  chain,
  bonus,
  totalDebt,
  totalCollateral,
}: Props) {
  const { getPoolByAddress } = useWeb3Context();

  const { position, getPositionParams } = usePoolData(poolAddress);

  const coeffs = usePoolCoeffs(poolAddress);
  const params = useApply(getPositionParams, coeffs);

  const poolInfo = getPoolByAddress(poolAddress);
  const company = poolInfo?.pool.protocol;
  const baseTokenSymbol = poolInfo?.baseToken?.symbol;
  const quoteTokenSymbol = poolInfo?.quoteToken?.symbol;
  const maturityDate = poolInfo?.pool.maturityDate;
  const haveSparkBonus = poolInfo?.pool.sparksPerQuotePerDay;

  const { days } = daysTillMaturity(maturityDate);

  const earnTokenSymbol = isBaseToken ? baseTokenSymbol : quoteTokenSymbol;
  const amount = isBaseToken ? params?.realBaseAmount : params?.realQuoteAmount;

  const { href } = getCardInfo({
    amount,
    chain,
    token: isBaseToken ? "base" : "quote",
    poolAddress: poolInfo?.pool.address,
    type: position?.type,
  });

  const haveBonus = Boolean(bonus?.length);
  return (
    <NextLink href={href}>
      <S.Wrapper>
        <S.MainPart>
          {haveBonus && (
            <S.BonusSectionDesktop>
              {bonus?.map((b) => (
                <Image
                  src={badgeByBonus[b]}
                  alt=""
                  key={b}
                  height={32}
                  width={32}
                />
              ))}
            </S.BonusSectionDesktop>
          )}
          <S.PictureContainer>
            <S.Bar>
              {haveSparkBonus && (
                <S.PictureBar>
                  <Image
                    src="/images/spark.svg"
                    alt=""
                    width={24}
                    height={24}
                  />
                  2&times;
                  <span className={haveBonus ? "haveBonus" : ""}>
                    &nbsp;sparks
                  </span>
                </S.PictureBar>
              )}
              {haveBonus && (
                <S.BonusSectionMobile>
                  {bonus?.map((b) => (
                    <Image
                      src={badgeByBonus[b]}
                      alt=""
                      key={b}
                      height={24}
                      width={24}
                    />
                  ))}
                </S.BonusSectionMobile>
              )}
            </S.Bar>
            <S.CoinImage>
              <Image
                layout="responsive"
                src={getTokenLargeImagePath(earnTokenSymbol)}
                width={160}
                height={160}
                alt=""
                className="coin"
              />
            </S.CoinImage>
          </S.PictureContainer>

          <S.PoolInfo>
            <T headerS>{getTokenSymbol(earnTokenSymbol)}</T>
            <T body secondary>
              {company ||
                `${getTokenSymbol(baseTokenSymbol)} / ${getTokenSymbol(
                  quoteTokenSymbol,
                )}`}
            </T>
            {days === null ? null : (
              <S.Days>
                <Image src="/images/clock.svg" width={20} height={20} alt="" />
                {daysDisplay(days)}
              </S.Days>
            )}
            <T headerL style={{ marginTop: "auto" }}>
              {formatPercent(apr)}
            </T>
          </S.PoolInfo>
        </S.MainPart>
        <PoolProgress
          collateral={totalCollateral}
          debt={totalDebt}
          tokenSymbol={earnTokenSymbol}
        />
      </S.Wrapper>
    </NextLink>
  );
}
